// src/components/staff/mobile/MobileStaffInvoices.jsx
// Accountant (20+): view + create + edit invoices, reports
// Cashier (20): process payments + view invoices only
// Uses hasMinLevel to differentiate Accountant vs Cashier

import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CreditCard, Plus, Eye, Download, CheckCircle, Clock, AlertCircle, TrendingUp, X, DollarSign } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { getAllInvoices, processPayment, getOutstandingBalances, getRevenueStats } from '../../../services/staffApi';

const GLOBAL_STYLE = `@keyframes spin { to { transform: rotate(360deg) } }`;

const STATUS = {
  pending: { label: 'Pending', bg: 'rgba(234,179,8,0.15)',  color: '#eab308', Icon: Clock },
  paid:    { label: 'Paid',    bg: 'rgba(34,197,94,0.15)',  color: '#22c55e', Icon: CheckCircle },
  overdue: { label: 'Overdue', bg: 'rgba(239,68,68,0.15)',  color: '#ef4444', Icon: AlertCircle },
};

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);
const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : '—';

const inputStyle = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 12, padding: '12px 14px', color: '#f1f5f9', fontSize: 15,
  outline: 'none', boxSizing: 'border-box',
  fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
};

// ─── Payment Sheet ────────────────────────────────────────────────────────────
const PaymentSheet = ({ open, invoice, onClose, onSaved }) => {
  const [form, setForm] = useState({ amount: '', method: 'credit_card', reference: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (invoice) setForm({ amount: invoice.amount ?? '', method: 'credit_card', reference: '' });
  }, [invoice]);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  const handlePay = async () => {
    if (!form.amount) { alert('Enter payment amount'); return; }
    setSaving(true);
    try {
      await processPayment(invoice.id, { amount: parseFloat(form.amount), method: form.method, reference: form.reference });
      onSaved();
      onClose();
    } catch (e) { alert('Error processing payment'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: '#1e293b', borderRadius: '24px 24px 0 0', maxHeight: '85dvh', display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)', animation: 'slideUp 0.28s cubic-bezier(0.32,0.72,0,1)' }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%) } to { transform: translateY(0) } }`}</style>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 4px' }}>
          <div style={{ width: 36, height: 4, borderRadius: 2, background: '#334155' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 20px 16px' }}>
          <span style={{ fontSize: 17, fontWeight: 700, color: '#f1f5f9' }}>Process Payment</span>
          <button onClick={onClose} style={{ background: '#334155', border: 'none', borderRadius: 20, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={15} color="#94a3b8" />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '0 20px 24px', flex: 1 }}>
          {/* Invoice summary */}
          <div style={{ background: '#0f172a', borderRadius: 14, padding: '14px', marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Invoice #{invoice?.invoice_number}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#f1f5f9' }}>{fmt(invoice?.amount)}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Due {fmtDate(invoice?.due_date)}</div>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Amount *</label>
            <input type="number" inputMode="decimal" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} style={inputStyle} placeholder="0.00" />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Payment Method</label>
            <select value={form.method} onChange={e => setForm({ ...form, method: e.target.value })} style={{ ...inputStyle, appearance: 'none' }}>
              <option value="credit_card">Credit Card</option>
              <option value="cash">Cash</option>
              <option value="check">Check</option>
              <option value="wire">Wire Transfer</option>
              <option value="account">On Account</option>
            </select>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>Reference / Note</label>
            <input type="text" value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} style={inputStyle} placeholder="Check #, card last 4…" />
          </div>
          <button onClick={handlePay} disabled={saving} style={{ width: '100%', padding: '16px', borderRadius: 16, background: saving ? '#166534' : '#22c55e', border: 'none', color: '#fff', fontSize: 16, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
            {saving ? 'Processing…' : '✓ Confirm Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Invoice Card ─────────────────────────────────────────────────────────────
const InvoiceCard = ({ inv, canEdit, onPay }) => {
  const sc = STATUS[inv.status] ?? STATUS.pending;
  return (
    <div style={{ background: '#1e293b', borderRadius: 18, border: '0.5px solid #334155', overflow: 'hidden' }}>
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: 'rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FileText size={18} color="#3b82f6" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9' }}>#{inv.invoice_number}</span>
              <span style={{ fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: sc.bg, color: sc.color, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                <sc.Icon size={9} /> {sc.label}
              </span>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', marginBottom: 2 }}>{fmt(inv.amount)}</div>
            <div style={{ fontSize: 11, color: '#475569' }}>
              {inv.client_name ?? 'Client'} · Issued {fmtDate(inv.created_at)}
              {inv.due_date ? ` · Due ${fmtDate(inv.due_date)}` : ''}
            </div>
          </div>
        </div>
      </div>
      {/* Actions */}
      <div style={{ display: 'flex', borderTop: '0.5px solid #334155' }}>
        <button style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#64748b', fontSize: 12 }}>
          <Eye size={14} /> View
        </button>
        <div style={{ width: '0.5px', background: '#334155' }} />
        <button style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#64748b', fontSize: 12 }}>
          <Download size={14} /> Download
        </button>
        {inv.status === 'pending' && (
          <>
            <div style={{ width: '0.5px', background: '#334155' }} />
            <button onClick={() => onPay(inv)} style={{ flex: 1.2, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#22c55e', fontSize: 12, fontWeight: 600 }}>
              <CreditCard size={14} /> Pay
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const MobileStaffInvoices = () => {
  const { hasMinLevel } = useAuth();
  const isAccountant = hasMinLevel(20);
  const canCreate = hasMinLevel(20); // both roles can view; accountant can create

  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [paySheet, setPaySheet] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [invRes, statsRes] = await Promise.all([
        getAllInvoices(filter === 'all' ? null : filter),
        getRevenueStats(),
      ]);
      setInvoices(invRes.data ?? []);
      setStats(statsRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending' },
    { id: 'paid', label: 'Paid' },
    { id: 'overdue', label: 'Overdue' },
  ];

  return (
    <div style={{ padding: '0 16px 100px' }}>
      <style>{GLOBAL_STYLE}</style>

      {/* Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '14px' }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>Pending</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#eab308' }}>{fmt(stats.pending_total)}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{stats.pending_count ?? 0} invoices</div>
          </div>
          <div style={{ background: '#1e293b', borderRadius: 16, border: '0.5px solid #334155', padding: '14px' }}>
            <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>This Month</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#22c55e' }}>{fmt(stats.revenue_month)}</div>
            <div style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>{stats.paid_count ?? 0} paid</div>
          </div>
          {isAccountant && (
            <div style={{ gridColumn: '1 / -1', background: '#1e293b', borderRadius: 16, border: '0.5px solid rgba(239,68,68,0.3)', padding: '14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: 10, color: '#64748b', marginBottom: 3 }}>Outstanding Balance</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#ef4444' }}>{fmt(stats.outstanding)}</div>
              </div>
              <TrendingUp size={24} color="rgba(239,68,68,0.4)" />
            </div>
          )}
        </div>
      )}

      {/* Filter pills */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            flex: 1, padding: '9px 4px', borderRadius: 12, border: 'none', cursor: 'pointer', fontSize: 11,
            fontWeight: filter === f.id ? 700 : 400,
            background: filter === f.id ? 'rgba(59,130,246,0.18)' : '#1e293b',
            color: filter === f.id ? '#3b82f6' : '#64748b',
            outline: filter === f.id ? '1px solid rgba(59,130,246,0.35)' : '0.5px solid #334155',
          }}>{f.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <div style={{ width: 28, height: 28, border: '2px solid #334155', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : invoices.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#1e293b', borderRadius: 20, border: '0.5px solid #334155' }}>
          <FileText size={36} color="#334155" style={{ marginBottom: 12 }} />
          <p style={{ color: '#475569' }}>No invoices found</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {invoices.map(inv => (
            <InvoiceCard key={inv.id} inv={inv} canEdit={isAccountant} onPay={setPaySheet} />
          ))}
        </div>
      )}

      {/* FAB — Accountant only */}
      {canCreate && (
        <button style={{
          position: 'fixed', bottom: 'calc(72px + env(safe-area-inset-bottom))', right: 20,
          width: 56, height: 56, borderRadius: 28, background: '#3b82f6', border: 'none',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 20px rgba(59,130,246,0.45)', zIndex: 50,
        }}>
          <Plus size={24} color="#fff" />
        </button>
      )}

      <PaymentSheet open={!!paySheet} invoice={paySheet} onClose={() => setPaySheet(null)} onSaved={load} />
    </div>
  );
};

export default MobileStaffInvoices;